import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  UncontrolledTooltip
} from "reactstrap";
import { Link } from "react-router-dom";
import TableContainer from "../../../../components/Common/TableContainer";
import Breadcrumbs from "../../../../components/Common/Breadcrumb";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { changeReimbursementHeadStatus, fetchReimbursementHeads, saveReimbursementHead, fetchReimbursementGridHeads } from "store/actions";
import Swal from "sweetalert2";
import Loader from "common/pageLoader";

const MasterHeads = () => {
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const {
    masterheads,
    gridheads,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    masterheads: state.ReimbursementMasterReducer?.masterheads ?? [],
    gridheads: state.ReimbursementMasterReducer?.gridheads ?? [],
    loading: state.ReimbursementMasterReducer?.loading,
    error: state.ReimbursementMasterReducer?.error,
    success: state.ReimbursementMasterReducer?.success,
  }));

  useEffect(() => {
    const authUserDetails = useAuthUserDetails();
    setAuthDetails(authUserDetails[0])
    const unitId = authUserDetails[0].unit_id;
    const userId = authUserDetails[0].user_id;
    dispatch(fetchReimbursementHeads(unitId, userId));
    dispatch(fetchReimbursementGridHeads(unitId));
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      dispatch(fetchReimbursementHeads(authDetails?.unit_id, authDetails?.user_id));
      dispatch(fetchReimbursementGridHeads(authDetails?.unit_id));
      formik.resetForm();
      setIsEdit(false);
      setEditId(null);
    }
  }, [authDetails, success, dispatch]);

  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      headsName: "",
    },
    validationSchema: Yup.object({
      headsName: Yup.string().required("Please Enter Head Name"),
    }),
    onSubmit: (values, { resetForm }) => {
      const formData = new FormData();
      formData.append("head_id", editId ? editId : 0);
      formData.append("heads_name", values.headsName);
      formData.append("create_by", authDetails?.user_id);
      dispatch(saveReimbursementHead(formData))
    },
  });

  const handleEdit = (row) => {
    formik.setValues({
      headsName: row.head_name,
    });
    setIsEdit(true);
    setEditId(row.head_id);
  };

  const handleCancel = () => {
    formik.resetForm();
    setIsEdit(false);
    setEditId(null);
  };

  const columns = [
    {
      Header: "S.No",
      Cell: ({ row }) => gridheads.length - row.index,
      className: "serial-no text-center",
      applyHeaderClass: true,
    },
    {
      Header: "Heads Name",
      accessor: "head_name",

    },
    {
      Header: "Action",
      className: "action-col",
      applyHeaderClass: true,
      Cell: (cellProps) => {
        const { b_active, head_id } = cellProps.row.original;
        const isActive = b_active === 1;
        return (
          <div className="d-flex gap-3">
            <Link
              to="#"
              style={{ color: isActive ? "green" : "red" }}
              onClick={() => handleToggleStatus(head_id, isActive)}
            >
              <i
                className={`mdi ${isActive ? "mdi-check-circle" : "mdi-close-circle"} font-size-18`}
                id={`status-tooltip-${cellProps.row.id}`}
              />
              <UncontrolledTooltip placement="top" target={`status-tooltip-${cellProps.row.id}`}>
                {isActive ? "Mark Inactive" : "Mark Active"}
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="text-success"
              onClick={() => handleEdit(cellProps.row.original)}
            >
              <i className="bx bxs-edit  font-size-18" id="edittooltip" />
              <UncontrolledTooltip placement="top" target="edittooltip">
                Edit
              </UncontrolledTooltip>
            </Link>
          </div>
        );
      },
    },
  ];

  const handleToggleStatus = (head_id, isActive) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to ${isActive ? 'deactivate' : 'activate'} this.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      reverseButtons: false,
      dangerMode: true,
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = new FormData();
        formData.append("head_id", head_id);
        formData.append("create_by", authDetails?.user_id);
        dispatch(changeReimbursementHeadStatus(formData));
      }
    });
  };

  const handleReset = () => {
    formik.handleReset();        // Resets form fields
    setIsEdit(false);            // Exit edit mode
    setEditId(null);             // Clear the edited ID
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Loader loading={loading} />
        <Container fluid>
          <Breadcrumbs title="Reimbursement" breadcrumbItem="Master Heads" />

          {/* Input Section */}
          <Card>
            <CardBody>
              <Form onSubmit={formik.handleSubmit}>
                <Row className="mb-3 ">
                  <Col md="4">
                    <FormGroup>
                      <Label for="headsName">
                        Heads Name <span className="text-danger">*</span></Label>
                      <Input
                        id="headsName"
                        name="headsName"
                        type="text"
                        value={formik.values.headsName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.headsName &&
                          Boolean(formik.errors.headsName)
                        }
                      />
                      {formik.touched.headsName && formik.errors.headsName && (
                        <div className="text-danger">{formik.errors.headsName}</div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="8" className="d-flex mob-center align-items-center justify-content-end mt-2">
                    <Button type="submit" className="btn button-save me-2">
                      <i className="fas fa-save me-1" /> {isEdit ? "Update" : "Save"}
                    </Button>
                    <Button
                      type="button"
                      onClick={handleReset}
                      className="btn button-cancel"> <i className="fas fa-sync me-1" /> Cancel
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>

          {/* Table Section */}
          <Card>
            <CardBody>
              <TableContainer
                columns={columns}
                data={gridheads && gridheads.length > 0 ? gridheads : []}
                isGlobalFilter={true}
                customPageSize={10}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default MasterHeads;
